<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import PixKeysAPI from 'dashboard/api/talkviaPixKeys';
import NextSelect from 'dashboard/components-next/select/Select.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { validPixAmount } from './pixPayment';

const props = defineProps({
  conversationId: { type: [Number, String], default: null },
  amount: { type: [String, Number], default: '' },
  description: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
});
const emit = defineEmits(['generated', 'invalidated']);
const { t } = useI18n();
const keys = ref([]);
const selectedId = ref('');
const loading = ref(true);
const generating = ref(false);
const error = ref('');
const loadFailed = ref(false);
const canGenerate = computed(
  () =>
    !props.disabled &&
    !loading.value &&
    !loadFailed.value &&
    !generating.value &&
    !!selectedId.value &&
    validPixAmount(props.amount)
);
let generation = 0;
let mounted = true;
const options = computed(() =>
  keys.value.map(key => ({
    value: key.id,
    label: `${key.label} · ${key.merchant_name}`,
  }))
);
const signature = computed(() =>
  JSON.stringify([
    props.conversationId,
    selectedId.value,
    validPixAmount(props.amount) ? Number(props.amount).toFixed(2) : '',
    props.description.trim() || 'PIX',
  ])
);
watch(signature, () => {
  generation += 1;
  emit('invalidated');
});

const load = async () => {
  loading.value = true;
  loadFailed.value = false;
  error.value = '';
  try {
    const { data } = await PixKeysAPI.get();
    if (!mounted) return;
    keys.value = data.payload;
    selectedId.value = keys.value[0]?.id || '';
  } catch {
    error.value = t('PIX_KEYS.LOAD_ERROR');
    loadFailed.value = true;
  } finally {
    loading.value = false;
  }
};

const generate = async () => {
  if (!canGenerate.value) return;
  generation += 1;
  const attempt = generation;
  const requestSignature = signature.value;
  generating.value = true;
  error.value = '';
  try {
    const { data } = await PixKeysAPI.generate(selectedId.value, {
      conversation_id: props.conversationId,
      amount: Number(props.amount).toFixed(2),
      description: props.description.trim() || undefined,
    });
    if (
      mounted &&
      generation === attempt &&
      signature.value === requestSignature
    )
      emit('generated', data);
  } catch (failure) {
    if (mounted && generation === attempt)
      error.value =
        failure?.response?.data?.error || t('PIX_KEYS.PAYMENT.ERROR');
  } finally {
    generating.value = false;
  }
};

onMounted(load);
onBeforeUnmount(() => {
  mounted = false;
  generation += 1;
});
defineExpose({ generate, canGenerate, generating });
</script>

<template>
  <div
    class="grid gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-3"
    data-test="saved-pix-key"
  >
    <div v-if="!compact">
      <p class="mb-1 text-sm font-semibold text-n-slate-12">
        {{ t('PIX_KEYS.PAYMENT.TITLE') }}
      </p>
      <p class="mb-0 text-xs text-n-slate-11">
        {{ t('PIX_KEYS.PAYMENT.HELP') }}
      </p>
    </div>
    <p v-if="loading" class="mb-0 text-xs text-n-slate-11" role="status">
      {{ t('PIX_KEYS.LOADING') }}
    </p>
    <p v-if="error" class="mb-0 text-xs text-n-ruby-11" role="alert">
      {{ error }}
    </p>
    <NextButton
      v-if="loadFailed"
      :label="t('PIX_KEYS.RETRY')"
      size="sm"
      variant="ghost"
      class="justify-self-start"
      @click="load"
    />
    <template v-if="!loading && !loadFailed">
      <p v-if="!keys.length" class="mb-0 text-xs text-n-slate-11">
        {{ t('PIX_KEYS.PAYMENT.EMPTY') }}
      </p>
      <template v-else>
        <label class="grid gap-1.5 text-sm font-medium text-n-slate-12">
          {{ t('PIX_KEYS.PAYMENT.SELECT') }}
          <NextSelect
            v-model="selectedId"
            :options="options"
            :disabled="disabled || generating"
            :aria-label="t('PIX_KEYS.PAYMENT.SELECT')"
          />
        </label>
        <NextButton
          v-if="!compact"
          :label="t('PIX_KEYS.PAYMENT.GENERATE')"
          icon="i-hugeicons-qr-code"
          size="sm"
          class="justify-self-start"
          :disabled="disabled || generating || !validPixAmount(amount)"
          :is-loading="generating"
          data-test="saved-pix-generate"
          @click="generate"
        />
        <p v-if="!compact" class="mb-0 text-xs text-n-slate-11">
          {{ t('PIX_KEYS.PAYMENT.NOTICE') }}
        </p>
      </template>
    </template>
  </div>
</template>
